import { ReactElement, useCallback, useMemo, useState } from 'react'
import { isPlanConfigVisibleForCommentFilter } from '@constants'
import { DrawControllerWrapper, OrthoSnapIndicator } from '@scene'
import { Vector3 } from 'three'
import { Delete, Edit, Height, OpenWith, Refresh } from '@mui/icons-material'
import useCreatePlanTable from '@editor/hooks/useCreatePlanTable'
import usePlanTableEditMode from '@editor/hooks/usePlanTableEditMode'
import usePlanTableMutations from '@editor/hooks/usePlanTableMutations'
import { ADD_PLAN_TABLES_SELECTION_KEY } from '@editor/hooks/usePlanTableTool'
import usePlanViewPlanTables from '@editor/pages/PlanView/hooks/usePlanViewPlanTables'
import { TransientDrawState, useCameraStore } from '@modugen/ui-toolkit'
import {
  getSelectedPlanElement,
  useCommentFilterStore,
  useControlStore,
  useModelStore,
  usePlanTableStore,
} from '@editorStores'
import {
  usePlanElementSelection,
  useClickActionEnabled,
  useMouseMovedSinceMouseDown,
  usePlanTableLayoutMetrics,
  useSelectedPlanElementId,
  useSelectionMode,
} from '@editorHooks'
import { computePlanTableBounds } from 'src/domain/planTables/positionAnchor'
import { computeTableLayoutDimensions } from 'src/domain/planTables/tableLayout'
import SceneHud, { SceneHudAction } from '../SceneHud'
import useCommentSnapTargets from '../hooks/useCommentSnapTargets'
import usePlanPositionSnapTargets from '../hooks/usePlanPositionSnapTargets'
import useScenePositionDrag, { ScenePosition } from '../hooks/useScenePositionDrag'
import PlanTableEditPopup from './PlanTableEditPopup'
import PlanTablePreview from './PlanTablePreview'

const PlanTableController = (): ReactElement => {
  const planTableStore = usePlanTableStore()
  const activeFilterViewId = usePlanTableStore(state => state.activeFilterViewId)
  const pendingTemplateKey = usePlanTableStore(state => state.pendingTemplateKey)
  const setPendingTemplateKey = usePlanTableStore(state => state.setPendingTemplateKey)
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const selectedId = useSelectedPlanElementId('table')
  const { isPlanView, getTableData, isExportedInPreview, refresh } = usePlanViewPlanTables({
    fetch: true,
  })
  const activeFilterViewTables = useMemo(
    () =>
      planTableStore.tables.filter(
        table =>
          table.filterViewId === activeFilterViewId &&
          // The selected table is always shown, regardless of the comment filter.
          (table.id === selectedId ||
            (isPlanConfigVisibleForCommentFilter(table.planConfig, selectedPlan) &&
              isExportedInPreview(table))),
      ),
    [planTableStore.tables, activeFilterViewId, selectedId, selectedPlan, isExportedInPreview],
  )
  // Every table with the content the plan draws for it, which also sets its size.
  const displayedTables = useMemo(
    () => activeFilterViewTables.map(table => ({ ...table, table: getTableData(table) })),
    [activeFilterViewTables, getTableData],
  )
  const { clearPlanSelection } = usePlanElementSelection()
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const snapToComments = useControlStore(state => state.snapToComments)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const tableLayoutMetrics = usePlanTableLayoutMetrics()
  const clickActionEnabled = useClickActionEnabled()
  const { selectionModeKey, unsetSelectionMode } = useSelectionMode()
  const { updatePlanTableFields, deletePlanTable } = usePlanTableMutations()
  const { createPlanTable } = useCreatePlanTable()
  const { enterPlanTableEdit, selectPlanTableInScene, exitPlanTableEdit } = usePlanTableEditMode()
  const { mouseMoved } = useMouseMovedSinceMouseDown()

  const [isEditing, setIsEditing] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const commentCategorySnapTargets = useCommentSnapTargets(isOrthographic)
  const additionalSnapTargets = useMemo(
    () => (snapToComments ? commentCategorySnapTargets : []),
    [snapToComments, commentCategorySnapTargets],
  )

  const { pointTargets: xySnapTargets, lineTargets: xyLineSnapTargets } =
    usePlanPositionSnapTargets(selectedId, {
      snapToCornersAndEdges,
      orthoSnap: snapOrthogonal,
    })

  const commitPosition = useCallback(
    (id: string, position: ScenePosition) => updatePlanTableFields(id, { position }),
    [updatePlanTableFields],
  )

  const { startDrag, dragPreview, activeDragMode, isDraggingRef } = useScenePositionDrag({
    storeyBoundaries,
    setIsDrawingActive,
    onCommit: commitPosition,
    xySnapTargets,
    xyLineSnapTargets,
  })

  const isAddMode = selectionModeKey === ADD_PLAN_TABLES_SELECTION_KEY

  const selectedTable = useMemo(
    () => activeFilterViewTables.find(table => table.id === selectedId) ?? null,
    [activeFilterViewTables, selectedId],
  )

  // Follow the live drag position while moving the selected table.
  const displayedSelectedTable = useMemo(() => {
    const table = displayedTables.find(item => item.id === selectedId)
    if (!table) return null
    if (dragPreview && dragPreview.id === table.id)
      return { ...table, position: dragPreview.position }
    return table
  }, [displayedTables, selectedId, dragPreview])

  const selectedTableDims = useMemo(
    () =>
      displayedSelectedTable
        ? computeTableLayoutDimensions(displayedSelectedTable.table, tableLayoutMetrics)
        : null,
    [displayedSelectedTable, tableLayoutMetrics],
  )

  const selectedTableBounds = useMemo(() => {
    if (!displayedSelectedTable || !selectedTableDims) return null
    return computePlanTableBounds(
      displayedSelectedTable.position,
      { width: selectedTableDims.totalWidth, height: selectedTableDims.totalHeight },
      displayedSelectedTable.positionAnchor,
    )
  }, [displayedSelectedTable, selectedTableDims])

  // Anchor the HUD below the table's bottom edge, centred horizontally.
  const hudPosition = useMemo(() => {
    if (!displayedSelectedTable || !selectedTableBounds) return null
    const { z } = displayedSelectedTable.position
    return new Vector3(
      (selectedTableBounds.xMin + selectedTableBounds.xMax) / 2,
      selectedTableBounds.yMin,
      z,
    )
  }, [displayedSelectedTable, selectedTableBounds])

  // Anchor the edit popup at the table's top-right corner.
  const popupAnchor = useMemo(() => {
    if (!displayedSelectedTable || !selectedTableBounds) return null
    const { z } = displayedSelectedTable.position
    return new Vector3(selectedTableBounds.xMax, selectedTableBounds.yMax, z)
  }, [displayedSelectedTable, selectedTableBounds])

  const handleDelete = useCallback(
    (id: string) => {
      setIsEditing(false)
      deletePlanTable(id)
      exitPlanTableEdit()
    },
    [deletePlanTable, exitPlanTableEdit],
  )

  // Clicking the scene away from the selected table deselects it. R3F reports a
  // click as a scene "miss" even when it lands on the HUD or ends a camera drag,
  // so skip those (HUD-button press, mouse moved, or an in-progress table drag).
  // Any other click away from it — including one on a model element — deselects.
  // The id guard keeps a miss of another table from clearing this one.
  const handlePointerMissed = useCallback(
    (event: MouseEvent, tableId: string) => {
      // Only a left click deselects; a right-button camera drag keeps the selection.
      if (event.button !== 0) return
      if (isDraggingRef.current || mouseMoved.current) return
      const selectedPlanElement = getSelectedPlanElement()
      if (selectedPlanElement?.type !== 'table' || selectedPlanElement.id !== tableId) return
      const target = event.target
      if (target instanceof Element && target.closest('.scene-hud-button')) return
      clearPlanSelection()
    },
    [isDraggingRef, mouseMoved, clearPlanSelection],
  )

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await refresh()
    } finally {
      setIsRefreshing(false)
    }
  }, [refresh])

  const hudGroups = useMemo<SceneHudAction[][]>(() => {
    if (!displayedSelectedTable) return []

    const moveActions: SceneHudAction[] = [
      {
        key: 'move-xy',
        tooltip: 'In der Ebene verschieben',
        icon: <OpenWith fontSize="small" />,
        onPointerDown: event =>
          startDrag('xy', displayedSelectedTable.id, displayedSelectedTable.position, event),
        active: activeDragMode === 'xy',
        dataCy: 'plan-table-move-xy',
      },
    ]
    if (!isOrthographic) {
      moveActions.push({
        key: 'move-z',
        tooltip: 'Vertikal verschieben (z)',
        icon: <Height fontSize="small" />,
        onPointerDown: event =>
          startDrag('z', displayedSelectedTable.id, displayedSelectedTable.position, event),
        active: activeDragMode === 'z',
        dataCy: 'plan-table-move-vertical',
      })
    }

    return [
      [
        ...moveActions,
        {
          key: 'edit',
          tooltip: 'Tabelle bearbeiten',
          icon: <Edit fontSize="small" />,
          onClick: () => setIsEditing(true),
          active: isEditing,
          dataCy: 'plan-table-edit',
        },
        {
          key: 'refresh',
          tooltip: 'Tabelleninhalte neu laden',
          icon: <Refresh fontSize="small" />,
          onClick: handleRefresh,
          loading: isRefreshing,
          dataCy: 'plan-table-refresh',
        },
      ],
      [
        {
          key: 'delete',
          tooltip: 'Tabelle löschen',
          icon: <Delete fontSize="small" />,
          onClick: () => handleDelete(displayedSelectedTable.id),
          variant: 'danger',
          dataCy: 'plan-table-delete',
        },
      ],
    ]
  }, [
    displayedSelectedTable,
    activeDragMode,
    isOrthographic,
    isEditing,
    isRefreshing,
    startDrag,
    handleRefresh,
    handleDelete,
  ])

  const handlePick = useCallback(
    async (point: Vector3) => {
      let z = point.z
      if (isOrthographic && floorplanStorey) {
        const boundary = storeyBoundaries[floorplanStorey]
        if (boundary) z = boundary[0]
      }

      const created = await createPlanTable(pendingTemplateKey, { x: point.x, y: point.y, z })

      if (created) {
        enterPlanTableEdit(created.id)
      }
      setPendingTemplateKey(null)
      unsetSelectionMode()
    },
    [
      createPlanTable,
      enterPlanTableEdit,
      floorplanStorey,
      isOrthographic,
      pendingTemplateKey,
      setPendingTemplateKey,
      storeyBoundaries,
      unsetSelectionMode,
    ],
  )

  if (!planTableStore.isVisible && !isAddMode) return <></>

  return (
    <>
      {isAddMode && (
        <DrawControllerWrapper
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          onDrawEnd={(state: TransientDrawState) => {
            if (state.drawPoint) handlePick(state.drawPoint.v)
          }}
          additionalSnapTargets={additionalSnapTargets}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
        />
      )}

      {planTableStore.isVisible &&
        displayedTables.map(table => {
          const display =
            dragPreview && dragPreview.id === table.id
              ? { ...table, position: dragPreview.position }
              : table
          return (
            <PlanTablePreview
              key={table.id}
              planTable={display}
              selected={selectedId === table.id}
              showContent={isPlanView}
              isDragging={dragPreview?.id === table.id}
              onClick={() => selectPlanTableInScene(table.id)}
              onPointerMissed={
                selectedId === table.id && !isEditing
                  ? event => handlePointerMissed(event, table.id)
                  : undefined
              }
            />
          )
        })}

      {planTableStore.isVisible && !isEditing && displayedSelectedTable && hudPosition && (
        <SceneHud position={hudPosition} groups={hudGroups} />
      )}

      {planTableStore.isVisible && isEditing && selectedTable && popupAnchor && (
        <PlanTableEditPopup
          planTable={selectedTable}
          anchor={popupAnchor}
          onClose={() => setIsEditing(false)}
        />
      )}

      {dragPreview?.isOrthoSnap && <OrthoSnapIndicator point={dragPreview.position} />}
    </>
  )
}

export default PlanTableController
